import { format, parseISO } from 'date-fns'

import { EMPTY_LIST } from '../../../../constants/common'
import { DATE_FULL_FORMAT_FROM_TIME } from '../../../../constants/date'
import { RENTAL_PORTAL_FACT_SEPARATOR } from '../../../../constants/rentalPortal'

import {
  APPLICANT_COMPLIANCE_CONNECTION_STATUS_CONNECTED,
  APPLICANT_COMPLIANCE_HANDOFF_BLOCKED_REASON,
  APPLICANT_COMPLIANCE_HANDOFF_BLOCKED_REASON_KEY,
  APPLICANT_COMPLIANCE_HANDOFF_LINE_KEY,
  APPLICANT_COMPLIANCE_HANDOFF_STATUS,
  APPLICANT_COMPLIANCE_HANDOFF_STATUS_KEY,
  APPLICANT_COMPLIANCE_HANDOFF_STATUS_TEXT_COLOR,
  APPLICANT_COMPLIANCE_LEGAL_BASIS_KEY,
  APPLICANT_COMPLIANCE_LIST_KEY,
  APPLICANT_COMPLIANCE_MATCH_COUNT_FALLBACK,
  APPLICANT_COMPLIANCE_NEUTRAL_TEXT_COLOR,
  APPLICANT_COMPLIANCE_NOT_STARTED_STATUS,
  APPLICANT_COMPLIANCE_SCREENING_LINE_KEY,
  APPLICANT_COMPLIANCE_SCREENING_STATUS_KEY,
  APPLICANT_COMPLIANCE_SCREENING_STATUS_TEXT_COLOR,
  QA_NAME_APPLICANT_PROFILE_COMPLIANCE_HANDOFF_DETAILS,
  QA_NAME_APPLICANT_PROFILE_COMPLIANCE_SCREENING_DETAILS,
} from './RentalPortalApplicantProfileCompliance.constants'

function getIsApplicantComplianceNotStarted(status) {
  return status === APPLICANT_COMPLIANCE_NOT_STARTED_STATUS
}

function getApplicantComplianceDateTime(value) {
  return value ? format(parseISO(value), DATE_FULL_FORMAT_FROM_TIME) : null
}

function getApplicantComplianceStatus({ statusKey, t, textColor }) {
  return {
    text: t(
      statusKey ?? 'RentalPortal.ApplicantProfileComplianceNotStartedKey'
    ),
    textColor: textColor ?? APPLICANT_COMPLIANCE_NEUTRAL_TEXT_COLOR,
  }
}

function getApplicantScreeningLines({ screening, t }) {
  const status = screening?.status ?? APPLICANT_COMPLIANCE_NOT_STARTED_STATUS

  if (getIsApplicantComplianceNotStarted(status)) {
    return [t('RentalPortal.ApplicantProfileComplianceScreeningEmptyKey')]
  }

  const screenedAt = getApplicantComplianceDateTime(screening.screened_at)
  const lineKey = APPLICANT_COMPLIANCE_SCREENING_LINE_KEY[status]

  const lines = []

  if (screenedAt) {
    lines.push(
      t('RentalPortal.ApplicantComplianceScreenedAtKey', { date: screenedAt })
    )
  }

  if (lineKey) {
    lines.push(
      t(lineKey, {
        date: getApplicantComplianceDateTime(screening.adjudicated_at),
        total: screening.match_count,
      })
    )
  }

  return lines
}

function getApplicantScreeningFacts({ screening, t }) {
  const lists = (screening.lists ?? EMPTY_LIST)
    .filter(list => APPLICANT_COMPLIANCE_LIST_KEY[list])
    .map(list => t(APPLICANT_COMPLIANCE_LIST_KEY[list]))

  const legalBasisKey =
    APPLICANT_COMPLIANCE_LEGAL_BASIS_KEY[screening.legal_basis]

  const facts = []

  if (screening.provider_display_name) {
    facts.push({
      key: 'RentalPortal.ApplicantComplianceProviderLabelKey',
      label: t('RentalPortal.ApplicantComplianceProviderLabelKey'),
      value: screening.provider_display_name,
    })
  }

  if (lists.length > 0) {
    facts.push({
      key: 'RentalPortal.ApplicantComplianceListsLabelKey',
      label: t('RentalPortal.ApplicantComplianceListsLabelKey'),
      value: lists.join(RENTAL_PORTAL_FACT_SEPARATOR),
    })
  }

  facts.push({
    key: 'RentalPortal.ApplicantComplianceMatchCountLabelKey',
    label: t('RentalPortal.ApplicantComplianceMatchCountLabelKey'),
    value: String(
      screening.match_count ?? APPLICANT_COMPLIANCE_MATCH_COUNT_FALLBACK
    ),
  })

  if (legalBasisKey) {
    facts.push({
      key: 'RentalPortal.ApplicantComplianceLegalBasisLabelKey',
      label: t('RentalPortal.ApplicantComplianceLegalBasisLabelKey'),
      value: t(legalBasisKey),
    })
  }

  return facts
}

function getIsApplicantHandoffConnectionReady(connection) {
  return connection?.status === APPLICANT_COMPLIANCE_CONNECTION_STATUS_CONNECTED
}

function getApplicantHandoffConnectionLine({ connection, t }) {
  if (!connection) {
    return t('RentalPortal.ApplicantComplianceHandoffConnectionMissingKey')
  }

  return t(
    getIsApplicantHandoffConnectionReady(connection)
      ? 'RentalPortal.ApplicantComplianceHandoffConnectionKey'
      : 'RentalPortal.ApplicantComplianceHandoffConnectionNotReadyKey',
    { label: connection.account_label }
  )
}

function getApplicantHandoffStatusLine({ handoff, t }) {
  if (getIsApplicantComplianceNotStarted(handoff.status)) {
    return t('RentalPortal.ApplicantProfileComplianceHandoffEmptyKey')
  }

  const lineKey = APPLICANT_COMPLIANCE_HANDOFF_LINE_KEY[handoff.status]
  const date = getApplicantComplianceDateTime(
    handoff.updated_at ?? handoff.dispatched_at
  )

  return lineKey && date ? t(lineKey, { date }) : null
}

function getIsApplicantHandoffFailed(handoff) {
  return handoff?.status === APPLICANT_COMPLIANCE_HANDOFF_STATUS.failed
}

function getApplicantHandoffLines({ handoff, t }) {
  const statusLine = getApplicantHandoffStatusLine({ handoff, t })

  const blockedReasonKey =
    APPLICANT_COMPLIANCE_HANDOFF_BLOCKED_REASON_KEY[handoff.blocked_reason]

  const lines = []

  if (statusLine) {
    lines.push(statusLine)
  }

  if (blockedReasonKey) {
    lines.push(t(blockedReasonKey))
  }

  if (getIsApplicantHandoffFailed(handoff) && handoff.last_error) {
    lines.push(handoff.last_error)
  }

  lines.push(
    getApplicantHandoffConnectionLine({ connection: handoff.connection, t })
  )

  return lines
}

function getApplicantHandoffFacts({ handoff, t }) {
  const hasAttempts = handoff.attempts > 0

  if (!handoff.external_id && !hasAttempts) {
    return EMPTY_LIST
  }

  const checkedAt = getApplicantComplianceDateTime(
    handoff.connection?.last_checked_at
  )

  const facts = []

  if (handoff.external_id) {
    facts.push({
      key: 'RentalPortal.ApplicantComplianceHandoffExternalIdLabelKey',
      label: t('RentalPortal.ApplicantComplianceHandoffExternalIdLabelKey'),
      value: handoff.external_id,
    })
  }

  if (hasAttempts) {
    facts.push({
      key: 'RentalPortal.ApplicantComplianceHandoffAttemptsLabelKey',
      label: t('RentalPortal.ApplicantComplianceHandoffAttemptsLabelKey'),
      value: String(handoff.attempts),
    })
  }

  if (handoff.connection?.payload_format) {
    facts.push({
      key: 'RentalPortal.ApplicantComplianceHandoffPayloadFormatLabelKey',
      label: t('RentalPortal.ApplicantComplianceHandoffPayloadFormatLabelKey'),
      value: handoff.connection.payload_format,
    })
  }

  if (checkedAt) {
    facts.push({
      key: 'RentalPortal.ApplicantComplianceHandoffConnectionCheckedLabelKey',
      label: t(
        'RentalPortal.ApplicantComplianceHandoffConnectionCheckedLabelKey'
      ),
      value: checkedAt,
    })
  }

  return facts
}

function getIsApplicantHandoffBlockedByErpConnection(handoff) {
  return (
    handoff?.blocked_reason ===
    APPLICANT_COMPLIANCE_HANDOFF_BLOCKED_REASON.erpConnection
  )
}

function getIsApplicantHandoffRetryRelevant(handoff) {
  return (
    getIsApplicantHandoffFailed(handoff) ||
    getIsApplicantHandoffBlockedByErpConnection(handoff)
  )
}

export function getHasApplicantComplianceScreening(applicantData) {
  return applicantData?.policy?.sanctions_screening_required
}

export function getHasApplicantComplianceHandoff(applicantData) {
  return applicantData?.policy?.erp_handoff_enabled
}

export function getApplicantScreeningBlock({ screening, t }) {
  const status = screening?.status ?? APPLICANT_COMPLIANCE_NOT_STARTED_STATUS

  const facts = getIsApplicantComplianceNotStarted(status)
    ? EMPTY_LIST
    : getApplicantScreeningFacts({ screening, t })

  return {
    disclosure:
      facts.length > 0
        ? {
            facts,
            qaName: QA_NAME_APPLICANT_PROFILE_COMPLIANCE_SCREENING_DETAILS,
            text: t('RentalPortal.ApplicantComplianceScreeningDetailsKey'),
          }
        : null,
    lines: getApplicantScreeningLines({ screening, t }),
    status: getApplicantComplianceStatus({
      statusKey: APPLICANT_COMPLIANCE_SCREENING_STATUS_KEY[status],
      t,
      textColor: APPLICANT_COMPLIANCE_SCREENING_STATUS_TEXT_COLOR[status],
    }),
  }
}

export function getApplicantHandoffBlock({ handoff, t }) {
  const status = handoff?.status ?? APPLICANT_COMPLIANCE_NOT_STARTED_STATUS

  const facts = handoff ? getApplicantHandoffFacts({ handoff, t }) : EMPTY_LIST

  return {
    disclosure:
      facts.length > 0
        ? {
            facts,
            note: t('RentalPortal.ApplicantComplianceHandoffPayloadScopeKey'),
            qaName: QA_NAME_APPLICANT_PROFILE_COMPLIANCE_HANDOFF_DETAILS,
            text: t(
              'RentalPortal.ApplicantComplianceHandoffTechnicalDetailsKey'
            ),
          }
        : null,
    lines: handoff
      ? getApplicantHandoffLines({ handoff, t })
      : [t('RentalPortal.ApplicantProfileComplianceHandoffEmptyKey')],
    status: getApplicantComplianceStatus({
      statusKey: APPLICANT_COMPLIANCE_HANDOFF_STATUS_KEY[status],
      t,
      textColor: APPLICANT_COMPLIANCE_HANDOFF_STATUS_TEXT_COLOR[status],
    }),
  }
}

export function getApplicantHandoffRetryAction({ handoff, t }) {
  if (!getIsApplicantHandoffRetryRelevant(handoff)) {
    return null
  }

  if (!handoff.permissions?.can_retry) {
    return {
      tooltip: t(
        'RentalPortal.ApplicantComplianceHandoffRetryNotAllowedTooltipKey'
      ),
    }
  }

  return getIsApplicantHandoffConnectionReady(handoff.connection)
    ? { tooltip: null }
    : {
        tooltip: t(
          'RentalPortal.ApplicantComplianceHandoffRetryNoConnectionTooltipKey'
        ),
      }
}
